import React from 'react';

import { className } from 'aplazame-design-system';
import { default as ButtonComponent } from 'aplazame-design-system/src/components/GatsbyTransitionButtonComponent';
import TransitionLink from 'aplazame-design-system/src/components/TransitionLink';

import style from './style.module.scss';

function HeaderDropdownItem({
    label,
    slug,
    description,
    type,
    openInNewWindow,
    onClick,
    ariaLabel
}) {
    return (
        <li
            className={
                slug ? style['col__item--link'] : style['col__item--title']
            }
        >
            {slug ? (
                type === 'button' ? (
                    <div
                        {...className(
                            `${style.item} ${style['button-wrapper']} d-flex align-items-center justify-content-between`
                        )}
                    >
                        {description ? (
                            <div>
                                <h4
                                    {...className(
                                        `${style['button-wrapper__description']} h4`
                                    )}
                                >
                                    {description}
                                </h4>
                            </div>
                        ) : null}
                        <div>
                            <ButtonComponent
                                button={{
                                    detached: true,
                                    externalLink: slug,
                                    block: true,
                                    label,
                                    onClick,
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <TransitionLink
                        {...className(
                            `${style.item} d-block w-100 text-decoration-none`
                        )}
                        activeClassName={style['link-active']}
                        to={slug}
                        title={label}
                        target={openInNewWindow ? '_blank' : null}
                        onClick={onClick}
                        aria-label={ariaLabel}
                    >
                        <span {...className(`body ${style['item__name']}`)}>
                            {label}
                        </span>
                        {description ? (
                            <span
                                {...className(
                                    `overline d-block ${style['item__description']}`
                                )}
                            >
                                {description}
                            </span>
                        ) : null}
                    </TransitionLink>
                )
            ) : (
                <h4
                    aria-hidden="true"
                    {...className(
                        `${style.item} ${style['item--title']} overline`
                    )}
                >
                    {label}
                </h4>
            )}
        </li>
    );
}

export default function HeaderDropdown({ items, onClick, className: classNameCmp }) {
    const fullWidthItems = items.filter(
        (itemGroup) => itemGroup[0].column % 10 === 0
    );
    const hasFullWidthButton =
        fullWidthItems &&
            fullWidthItems.length &&
            fullWidthItems[0].length &&
            fullWidthItems[0][0].type === 'button'
            ? true
            : false;
    const otherItems = items.filter(
        (itemGroup) => itemGroup[0].column % 10 !== 0
    );

    function getListTitle(items) {
        const itemsWithNullSlug = items.filter(item => item.slug == '');
        return itemsWithNullSlug.length > 0 ? itemsWithNullSlug[0].label : null;
    }

    // Helper function to split items into sections based on headings (items with no slug)
    function splitIntoSections(items) {
        const sections = [];
        let currentSection = [];

        items.forEach((item, index) => {
            if (!item.slug && currentSection.length > 0) {
                // Found a heading and we have items in current section, finish current section
                sections.push(currentSection);
                currentSection = [item]; // Start new section with the heading
            } else {
                // Add item to current section
                currentSection.push(item);
            }
        });

        // Add the last section if it has items
        if (currentSection.length > 0) {
            sections.push(currentSection);
        }

        return sections;
    }

    // Function to get aria label for links grouped by sections
    const getGroupedAriaLabel = (items, currentIndex) => {
        if (!items[currentIndex].slug) return undefined; // No aria-label for headings

        // Find the next heading (or end of array) to define the current section
        let sectionEndIndex = items.length;
        for (let j = currentIndex + 1; j < items.length; j++) {
            if (!items[j].slug) {
                sectionEndIndex = j;
                break;
            }
        }

        // Find the previous heading (or start of array) to define the current section start
        let sectionStartIndex = 0;
        for (let j = currentIndex - 1; j >= 0; j--) {
            if (!items[j].slug) {
                sectionStartIndex = j + 1;
                break;
            }
        }

        // Get only the links in this section
        const sectionItems = items.slice(sectionStartIndex, sectionEndIndex);
        const sectionLinks = sectionItems.filter(item => item.slug);

        // Find position of current item within this section's links
        const positionInSection = sectionLinks.findIndex(link => link === items[currentIndex]) + 1;

        return `${items[currentIndex].label} ${positionInSection}/${sectionLinks.length}`;
    };

    return (
        <div
            {...className(
                `${style.wrapper} ${style[
                hasFullWidthButton
                    ? 'wrapper--with-button'
                    : 'wrapper--without-button'
                ]
                } ${style[`wrapper--for-${otherItems.length}-col`]}
                ${classNameCmp ? classNameCmp : ""}`
            )}
        >
            {fullWidthItems && fullWidthItems.length ? (
                <div>
                    {splitIntoSections(fullWidthItems[0]).map((section, sectionIndex) => (
                        <ul key={sectionIndex} {...className('list-unstyled')}>
                            {section.map((item, i) => {
                                // Calculate the original index for aria-label calculation
                                const originalIndex = fullWidthItems[0].findIndex(originalItem => originalItem === item);
                                const ariaLabel = getGroupedAriaLabel(fullWidthItems[0], originalIndex);

                                return (
                                    <HeaderDropdownItem
                                        ariaLabel={ariaLabel}
                                        index={originalIndex}
                                        key={originalIndex}
                                        {...item}
                                        onClick={
                                            item.onClick
                                                ? (e) => {
                                                    item.onClick(e);
                                                    onClick(e);
                                                }
                                                : onClick
                                                    ? onClick
                                                    : null
                                        }
                                    />
                                );
                            })}
                        </ul>
                    ))}
                </div>
            ) : null}
            {otherItems && otherItems.length ? (
                <div {...className(`d-flex flex-column flex-lg-row`)}>
                    {otherItems.map((items, i) => {
                        return (
                            <div key={i} {...className(style.cols)}>
                                {splitIntoSections(items).map((section, sectionIndex) => (
                                    <ul
                                        key={sectionIndex}
                                        {...className('list-unstyled')}
                                        title={getListTitle(section)}
                                    >
                                        {section.map((item, ii) => {
                                            // Calculate the original index for aria-label calculation
                                            const originalIndex = items.findIndex(originalItem => originalItem === item);
                                            const ariaLabel = getGroupedAriaLabel(items, originalIndex);

                                            return (
                                                <HeaderDropdownItem
                                                    ariaLabel={ariaLabel}
                                                    key={originalIndex}
                                                    {...item}
                                                    onClick={
                                                        item.onClick
                                                            ? (e) => {
                                                                item.onClick(e);
                                                                onClick(e);
                                                            }
                                                            : onClick
                                                                ? onClick
                                                                : null
                                                    }
                                                />
                                            );
                                        })}
                                    </ul>
                                ))}
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
